import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';
import { LabelResult } from 'stores/LabelStoreAdapter';

// --- 定数 ---

/**
 * 公開設定（visibility）。
 * master_type ではないためフロント定数で保持する（tech_form_components.md 準拠）。
 */
export const ArticleVisibility = {
  PUBLIC: '公開',
  INTERNAL: '社内公開',
  PRIVATE: '非公開',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ArticleVisibility = keyof typeof ArticleVisibility;

// --- 型定義 ---

/** 記事の著者（userName のみ。内部 user_id は秘匿） */
export type ArticleAuthor = {
  userName: string;
};

/** 記事一覧の1要素（API6201） */
export type ArticleSummary = {
  articleId: string;
  /**
   * 本文参照先の fileId。
   * ワークスペースで「記事公開済みファイル」を可視化するために利用する（バックエンドが返す場合のみ）。
   * 返さない場合は undefined（可視化はファイルを開いた際の articleIds で補完される）。
   */
  fileId?: string;
  title: string;
  subtitle?: string;
  description?: string;
  visibility: ArticleVisibility;
  /** 著者（sort_order 順） */
  authors: ArticleAuthor[];
  /** ラベル（論理削除済みも含む） */
  labels: LabelResult[];
  /** 本文文字数（readTime 換算用。NULL 許可） */
  charCount: number | null;
  /** 公開日 */
  createdAt: string;
  /** セッションの user_id が created_by と一致するか */
  editable: boolean;
};

/** 記事詳細（API6202）。一覧項目 + 本文取得用の fileId / extension */
export type ArticleDetail = ArticleSummary & {
  /** 本文参照先（本文取得は API6111） */
  fileId: string;
  /** 拡張子（md / knz。本文レンダリングの出し分け用） */
  extension: string;
};

/** 記事一覧取得リクエスト（API6201） */
export type ListArticleParams = {
  /** 曖昧検索キーワード（search_text 部分一致） */
  keyword?: string;
  /** ラベル絞り込み（OR） */
  labelIds?: string[];
  /** 著者絞り込み（OR） */
  authorUserIds?: string[];
  /** true: 全件取得（グループ化用）/ false or 未指定: ページング */
  grouping?: boolean;
  /** 全記事モードのページ番号 */
  page?: number;
  /** 全記事モードの1ページ件数（未指定時はサーバ既定 article.page-size） */
  pageSize?: number;
};

/** 記事一覧取得レスポンス（API6201） */
export type ListArticleResponse = {
  articles: ArticleSummary[];
  /** 全記事モードのみ返る総件数 */
  totalCount?: number;
};

/** 公開用ファイル詳細（API6111） */
export type PublicArticleFile = {
  fileId: string;
  extension: string;
  detail: string;
};

export type CreateArticleParams = {
  fileId: string;
  title: string;
  subtitle?: string;
  description?: string;
  authorUserIds: string[];
  labelIds: string[];
  visibility: ArticleVisibility;
};

export type UpdateArticleParams = {
  articleId: string;
  title: string;
  subtitle?: string;
  description?: string;
  authorUserIds: string[];
  labelIds: string[];
  visibility: ArticleVisibility;
};

export type DeleteArticleParams = {
  articleId: string;
};

export type ChangeArticleVisibilityParams = {
  articleId: string;
  visibility: ArticleVisibility;
};

/**
 * 記事反映（API6211）リクエスト。
 * 保存/反映分離（R-11）により、fileId を参照する自分の記事へ、
 * フロントが knz FrontMatter から抽出したメタを明示的に同期する。
 */
export type ReflectArticleParams = {
  /** 本文参照先の fileId（必須） */
  fileId: string;
  /** タイトル（必須・FrontMatter から抽出） */
  title: string;
  /** サブタイトル（任意） */
  subtitle?: string;
  /** 説明（任意） */
  description?: string;
  /** 公開設定（任意・省略時はサーバ側で既存維持） */
  visibility?: ArticleVisibility;
  /** 著者の userId（任意・FrontMatter から抽出） */
  authorUserIds?: string[];
};

/**
 * セッションIDヘッダー。
 * 公開系（一覧 / 詳細 / 公開用ファイル）でも session があれば付与する（ログイン状態の参照範囲判定用）。
 * ゲスト時は null になるが、公開APIは未認証で通る。
 */
const sessionHeaders = () => ({
  headers: {
    'X-Session-Id': localStorage.getItem('session-id'),
  },
});

// --- API関数 ---

/**
 * 記事一覧取得API（API6201 / 未ログイン可）
 * grouping=false でページング、grouping=true で全件取得（グルーピングはフロント）。
 * @param params.keyword - 曖昧検索キーワード（任意, search_text 部分一致）
 * @param params.labelIds - ラベル絞り込み（任意, OR）
 * @param params.authorUserIds - 著者絞り込み（任意, OR）
 * @param params.grouping - true=全件取得 / false・未指定=ページング（任意）
 * @param params.page - ページ番号（任意, 全記事モード）
 * @param params.pageSize - 1ページ件数（任意, 全記事モード）
 */
export const listArticle = async (params: ListArticleParams): Promise<ListArticleResponse> => {
  try {
    const response = await axios.post(KanzhongBackend.LIST_ARTICLE, params, sessionHeaders());
    return {
      // API6201 のレスポンスは results（記事配列）+ totalCount
      articles: response.data.results ?? [],
      totalCount: response.data.totalCount ?? undefined,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 記事詳細取得API（API6202 / 未ログイン可）
 * 本文は含まない（本文は getPublicArticleFile で別取得）。参照不可 / 非存在は 404。
 * @param articleId - 記事ID（必須, 最大64文字）
 */
export const getArticle = async (articleId: string): Promise<ArticleDetail> => {
  try {
    const response = await axios.get(
      `${KanzhongBackend.GET_ARTICLE}/${articleId}`,
      sessionHeaders(),
    );
    return response.data.result ?? response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ファイル詳細取得(公開用)API（API6111 / 未ログイン可）
 * その fileId を参照する記事の中に参照可能な公開設定のものが1件以上あれば取得可。該当なしは 404。
 * @param fileId - ファイルID（必須, 最大64文字）
 */
export const getPublicArticleFile = async (fileId: string): Promise<PublicArticleFile> => {
  try {
    const response = await axios.get(
      `${KanzhongBackend.GET_PUBLIC_ARTICLE_FILE}/${fileId}`,
      sessionHeaders(),
    );
    return response.data.result ?? response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 記事登録API（API6203 / 認証のみ）
 * @param params.fileId - 本文参照先の file_id（必須, md / knz のみ）
 * @param params.title - タイトル（必須, 最大128文字）
 * @param params.subtitle - サブタイトル（任意, 最大1024文字）
 * @param params.description - 説明（任意, 最大1024文字）
 * @param params.authorUserIds - 著者の user_id（必須, 1件以上, 並び順保持）
 * @param params.labelIds - label_id（必須, 1件以上）
 * @param params.visibility - PUBLIC / INTERNAL / PRIVATE（必須）
 * @returns 作成した articleId
 */
export const createArticle = async (params: CreateArticleParams): Promise<string> => {
  try {
    const response = await axios.post(KanzhongBackend.CREATE_ARTICLE, params, sessionHeaders());
    return response.data.articleId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 記事更新API（API6204 / 認証のみ・created_by 本人）
 * fileId は変更不可のため受け取らない。
 * @param params.articleId - 記事ID（必須, 最大64文字）
 * @param params.title - タイトル（必須, 最大128文字）
 * @param params.subtitle - サブタイトル（任意, 最大1024文字）
 * @param params.description - 説明（任意, 最大1024文字）
 * @param params.authorUserIds - 著者の user_id（必須, 1件以上）
 * @param params.labelIds - label_id（必須, 1件以上）
 * @param params.visibility - PUBLIC / INTERNAL / PRIVATE（必須）
 * @returns 対象の articleId
 */
export const updateArticle = async (params: UpdateArticleParams): Promise<string> => {
  try {
    const response = await axios.post(KanzhongBackend.UPDATE_ARTICLE, params, sessionHeaders());
    return response.data.articleId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 記事削除API（API6205 / 認証のみ・created_by 本人）
 * 物理削除（article_author / article_label も連鎖）。file_detail は削除しない。
 * @param params.articleId - 記事ID（必須, 最大64文字）
 * @returns 削除した articleId
 */
export const deleteArticle = async (params: DeleteArticleParams): Promise<string> => {
  try {
    const response = await axios.post(KanzhongBackend.DELETE_ARTICLE, params, sessionHeaders());
    return response.data.articleId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 記事公開設定変更API（API6206 / 認証のみ・created_by 本人）
 * @param params.articleId - 記事ID（必須, 最大64文字）
 * @param params.visibility - PUBLIC / INTERNAL / PRIVATE（必須）
 * @returns 対象の articleId
 */
export const changeArticleVisibility = async (
  params: ChangeArticleVisibilityParams,
): Promise<string> => {
  try {
    const response = await axios.post(
      KanzhongBackend.CHANGE_ARTICLE_VISIBILITY,
      params,
      sessionHeaders(),
    );
    return response.data.articleId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 記事反映API（API6211 / 認証のみ・created_by 本人）
 * 保存/反映分離（R-11）。fileId を参照する自分の記事に、
 * フロントが knz FrontMatter からパースして抽出したメタを明示的に同期する。
 * 保存（API6107）とは分離した操作で、workspace の `[記事に反映]` から呼ぶ。
 * バックエンドは knz をパースしない（フロントが構造化データを渡す）。
 * @param params.fileId - 本文参照先の file_id（必須, 最大64文字）
 * @param params.title - タイトル（必須, 最大128文字）
 * @param params.subtitle - サブタイトル（任意, 最大1024文字）
 * @param params.description - 説明（任意, 最大1024文字）
 * @param params.visibility - PUBLIC / INTERNAL / PRIVATE（任意）
 * @param params.authorUserIds - 著者の user_id（任意, 並び順保持）
 * @returns 反映した記事IDリスト（該当なしは空配列）
 */
export const reflectArticle = async (params: ReflectArticleParams): Promise<string[]> => {
  try {
    const response = await axios.post(KanzhongBackend.REFLECT_ARTICLE, params, sessionHeaders());
    return response.data.articleIds ?? [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
